import { useState, useEffect, useRef, useCallback } from "react";
import {
  ClipboardCheck,
  Clock,
  BarChart3,
  Play,
  Target,
  FileCheck,
  CheckCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  fetchGovernancePulseAssessments,
  fetchPulseRequests,
} from "../../services/businessDashApi";
import GovernancePulseAssessment from "./GovernancePulseAssessment";

/** Anchor for deep-links from Board Actions */
export const GOVERNANCE_PULSE_ANCHOR_ID = "governance-pulse-section";

const brandBtn =
  "flex items-center gap-2 h-12 px-8 text-base !bg-[rgba(9,54,107,0.72)] !text-white hover:!bg-[rgba(9,54,107,0.88)] focus-visible:!ring-[rgba(9,54,107,0.45)]";

const BoardGovernancePulse = ({ isChair = false, autoStartAssessment = false }) => {
  const [showAssessment, setShowAssessment] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingPulse, setLoadingPulse] = useState(false);
  const [latestAssessment, setLatestAssessment] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(true);
  const [linkedPulseRequestId, setLinkedPulseRequestId] = useState(null);
  const sectionRef = useRef(null);

  /** Optional deep-link: set `sessionStorage.setItem('az_pulse_request_id', idOrSlug)` before opening Governance tab */
  useEffect(() => {
    try {
      const v = sessionStorage.getItem("az_pulse_request_id");
      if (v) {
        setLinkedPulseRequestId(v);
        sessionStorage.removeItem("az_pulse_request_id");
      }
    } catch {
      /* ignore */
    }
  }, []);

  const loadLatest = useCallback(async () => {
    setLoadingLatest(true);
    try {
      const raw = await fetchGovernancePulseAssessments({
        ordering: "-assessment_date",
        limit: 1,
      });
      const list = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw)
          ? raw
          : [];
      setLatestAssessment(list[0] ?? null);
    } catch {
      setLatestAssessment(null);
    } finally {
      setLoadingLatest(false);
    }
  }, []);

  useEffect(() => {
    if (autoStartAssessment) setShowAssessment(true);
  }, [autoStartAssessment]);

  useEffect(() => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    if (!isChair) return;
    const load = async () => {
      setLoadingPulse(true);
      try {
        const data = await fetchPulseRequests();
        const list = Array.isArray(data) ? data : data?.results ?? data?.data ?? [];
        setPendingRequests(list);
      } catch {
        setPendingRequests([]);
      } finally {
        setLoadingPulse(false);
      }
    };
    load();
  }, [isChair]);

  useEffect(() => {
    void loadLatest();
  }, [loadLatest]);

  const lastCompletedAt =
    latestAssessment?.completed_at ??
    latestAssessment?.assessment_date ??
    latestAssessment?.created_at;

  return (
    <div id={GOVERNANCE_PULSE_ANCHOR_ID} ref={sectionRef} className="space-y-8">
      <Card className="overflow-hidden border-primary/20 shadow-lg">
        <div className="bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5 p-8">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-primary/15 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <ClipboardCheck className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">Governance Pulse</h2>
                <p className="text-base text-foreground/70">
                  A periodic check-in on role clarity, decision quality and governance discipline.
                </p>
              </div>
              <div className="text-sm text-foreground/80 leading-relaxed space-y-2">
                <p>
                  Governance Pulse is a structured self-assessment that helps the board evaluate its
                  governance practices across key dimensions. Results are tracked over time to support
                  continuous improvement and documented evidence for reporting.
                </p>
              </div>
              <ul className="space-y-2 text-sm text-foreground/80">
                <li className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Role clarity & mandates</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Decision quality & follow-up</span>
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Cadence & discipline</span>
                </li>
              </ul>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Badge variant="muted" className="text-xs font-normal">
                  3 themes
                </Badge>
                <Badge variant="muted" className="text-xs font-normal">
                  11 questions
                </Badge>
                <Badge variant="muted" className="text-xs font-normal">
                  ≈10 min
                </Badge>
                <Badge variant="muted" className="text-xs font-normal">
                  Full anonymity
                </Badge>
              </div>
              {!loadingLatest && lastCompletedAt && (
                <div className="flex items-center gap-2 text-sm text-primary pt-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    Last completed:{" "}
                    {new Date(lastCompletedAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
              )}
              {isChair && !loadingPulse && pendingRequests.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-primary pt-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    {pendingRequests.length} pulse request
                    {pendingRequests.length !== 1 ? "s" : ""} pending your review (see Board Actions)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-center gap-6 flex-wrap">
        <Button size="lg" type="button" onClick={() => setShowAssessment(true)} className={brandBtn}>
          <Play className="w-5 h-5" />
          Take Pulse
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="outline"
                size="lg"
                type="button"
                disabled={!isChair || !latestAssessment}
                className="flex items-center gap-2 h-12 px-8 text-base"
              >
                <BarChart3 className="w-5 h-5" />
                Chair Summary
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {!isChair
                ? "Chair only"
                : !latestAssessment
                  ? "Complete a Pulse to unlock aggregated chair view when the backend exposes summary data."
                  : "Open Chair Brief from the banner above when available."}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>

      <GovernancePulseAssessment
        isOpen={showAssessment}
        onClose={() => setShowAssessment(false)}
        pulseRequestId={linkedPulseRequestId}
        onComplete={() => {
          void loadLatest();
          setLinkedPulseRequestId(null);
        }}
        onSave={(progress) => {
          console.log("Governance Pulse progress:", progress);
        }}
      />
    </div>
  );
};

export default BoardGovernancePulse;
